import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api, APIError } from '../lib/api'
import { useAuth } from '../lib/auth'
import { ProcessBadge } from '../components/ProcessBadge'

interface SimilarHit {
  idea_id: string
  code: string
  title: string
  snippet: string
  similarity: number
  reason: string
  process_tags: string[]
}

interface SimilarResponse {
  process_tags: string[]
  similar: SimilarHit[]
}

function similarityColorClasses(score: number): string {
  if (score >= 80) return 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-200 dark:border-red-700'
  if (score >= 70) return 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/40 dark:text-orange-200 dark:border-orange-700'
  return 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-200 dark:border-yellow-700'
}

interface Department {
  id: string
  code: string
  name: string
  business_unit_id: string | null
}

export const Route = createFileRoute('/submit')({
  beforeLoad: async () => {
    const s = useAuth.getState()
    if (s.loading) await s.load()
    if (!useAuth.getState().user) {
      throw redirect({ to: '/login' })
    }
  },
  component: SubmitPage,
})

const MIN_DESCRIPTION = 50

function SubmitPage() {
  const navigate = useNavigate()
  const user = useAuth((s) => s.user)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [targetDepartmentId, setTargetDepartmentId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tips, setTips] = useState<string[]>([])
  const [tipsLoading, setTipsLoading] = useState(false)
  const [similar, setSimilar] = useState<SimilarResponse | null>(null)
  const [similarLoading, setSimilarLoading] = useState(false)

  // Автоподстановка подразделения автора как target (можно поменять)
  useEffect(() => {
    if (user?.department_id && !targetDepartmentId) {
      setTargetDepartmentId(user.department_id)
    }
  }, [user, targetDepartmentId])

  const { data: departments } = useQuery({
    queryKey: ['lookups', 'departments'],
    queryFn: () => api<Department[]>('/api/lookups/departments'),
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  const tooShort = description.length < MIN_DESCRIPTION
  const noTitle = !title.trim()
  // Если форма готова для similarity-вызова, кнопка ждёт пока он завершится
  // (similar !== null OR similarLoading=false с попыткой → даём submit).
  // Простое правило: блокируем только если similarLoading=true (запрос в полёте).
  const waitingForSimilarity =
    !tooShort && title.trim().length >= 3 && (similarLoading || similar === null)

  useEffect(() => {
    if (noTitle || description.length < 30) {
      setTips([])
      return
    }
    const handle = window.setTimeout(async () => {
      try {
        setTipsLoading(true)
        const resp = await api<{ tips: string[] }>('/api/ai/tips', {
          method: 'POST',
          body: JSON.stringify({ title: title.trim(), description: description.trim() }),
        })
        setTips(resp.tips)
      } catch {
        setTips([])
      } finally {
        setTipsLoading(false)
      }
    }, 800)
    return () => window.clearTimeout(handle)
  }, [title, description, noTitle])

  useEffect(() => {
    const trimmedTitle = title.trim()
    if (trimmedTitle.length < 3 || description.length < 50) {
      setSimilar(null)
      setSimilarLoading(false)
      return
    }
    let cancelled = false
    const handle = window.setTimeout(async () => {
      try {
        setSimilarLoading(true)
        const resp = await api<SimilarResponse>('/api/ideas/similar', {
          method: 'POST',
          body: JSON.stringify({ title: trimmedTitle, description: description.trim() }),
        })
        if (!cancelled) setSimilar(resp)
      } catch {
        if (!cancelled) setSimilar(null)
      } finally {
        if (!cancelled) setSimilarLoading(false)
      }
    }, 1200)
    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [title, description])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const body: Record<string, unknown> = {
        title: title.trim(),
        description: description.trim(),
      }
      if (targetDepartmentId) body.target_department_id = targetDepartmentId
      const created = await api<{ id: string }>('/api/ideas', {
        method: 'POST',
        body: JSON.stringify(body),
      })
      void navigate({ to: '/ideas/$id', params: { id: created.id } })
    } catch (err) {
      if (err instanceof APIError && err.status === 422) {
        setError('Описание должно быть не короче 50 символов')
      } else {
        setError('Не удалось отправить идею')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto p-8">
      <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 mb-6">
        Подать идею
      </h1>
      <form
        onSubmit={handleSubmit}
        className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-6 space-y-4"
      >
        <div>
          <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
            Название
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={500}
            required
            className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
            Подразделение, к которому относится идея
          </label>
          {departments && departments.length > 0 ? (
            <select
              value={targetDepartmentId}
              onChange={(e) => setTargetDepartmentId(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 text-sm"
            >
              <option value="">— не указано —</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-xs text-zinc-500 italic">
              Справочник подразделений недоступен — поле необязательное
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
            Описание
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Опиши проблему, своё решение и ожидаемый эффект"
            rows={10}
            maxLength={10000}
            required
            className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 text-sm"
          />
          <div className="mt-1 text-xs text-zinc-500 flex justify-between">
            <span>
              {tooShort
                ? `Минимум ${MIN_DESCRIPTION} символов (ещё ${MIN_DESCRIPTION - description.length})`
                : 'Описание принято'}
            </span>
            <span>
              {description.length} / 10000
            </span>
          </div>
        </div>

        {similarLoading && !similar && (
          <div className="text-xs text-zinc-500 dark:text-zinc-400">
            🔍 Ищем похожие идеи…
          </div>
        )}

        {similar && similar.process_tags.length > 0 && (
          <div className="text-xs text-zinc-700 dark:text-zinc-300">
            <p className="mb-1 font-medium">Классифицировано как:</p>
            <div className="flex flex-wrap gap-1">
              {similar.process_tags.map((code) => (
                <ProcessBadge key={code} code={code} size="sm" />
              ))}
            </div>
          </div>
        )}

        {similar && similar.similar.length === 0 && (
          <div className="text-xs text-green-700 dark:text-green-400">
            ✅ Похожих идей не нашли — это, видимо, новая тема.
          </div>
        )}

        {similar && similar.similar.length > 0 && (
          <div className="border border-amber-300 dark:border-amber-600 rounded-md p-3 text-xs text-zinc-700 dark:text-zinc-300 space-y-2">
            <p className="font-semibold">⚠️ Возможно, такая идея уже есть</p>
            <ul className="space-y-2">
              {similar.similar.map((hit) => (
                <li
                  key={hit.idea_id}
                  className="border border-zinc-200 dark:border-zinc-700 rounded-md p-2 bg-white dark:bg-zinc-900"
                >
                  <Link
                    to="/ideas/$id"
                    params={{ id: hit.idea_id }}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block hover:bg-zinc-50 dark:hover:bg-zinc-800 -m-2 p-2 rounded-md"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-mono text-zinc-500">{hit.code}</p>
                        <p className="font-medium text-zinc-900 dark:text-zinc-100 truncate">
                          {hit.title}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${similarityColorClasses(hit.similarity)}`}
                      >
                        {hit.similarity}%
                      </span>
                    </div>
                    {hit.reason && (
                      <p className="mt-1 italic text-[11px] text-zinc-500 dark:text-zinc-400">
                        {hit.reason}
                      </p>
                    )}
                    {hit.process_tags.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {hit.process_tags.map((code) => (
                          <ProcessBadge key={code} code={code} />
                        ))}
                      </div>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(tipsLoading || tips.length > 0) && (
          <div className="border border-dashed border-amber-300 dark:border-amber-600 rounded-md p-3 text-xs text-zinc-700 dark:text-zinc-300">
            <p className="font-semibold mb-2">
              🤖 AI-ассистент {tipsLoading ? '(думает…)' : 'предлагает'}
            </p>
            {tips.length > 0 && (
              <ul className="list-disc list-inside space-y-1">
                {tips.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => void navigate({ to: '/my-ideas' })}
            className="px-4 py-2 text-sm text-zinc-600 dark:text-zinc-400"
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={busy || tooShort || noTitle || waitingForSimilarity}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md text-sm"
            title={
              waitingForSimilarity
                ? 'Подождите — система проверяет, нет ли уже похожих идей…'
                : undefined
            }
          >
            {busy
              ? 'Отправляю…'
              : waitingForSimilarity
                ? '🔍 Проверка дублей…'
                : 'Подать идею'}
          </button>
        </div>
      </form>
    </div>
  )
}
